// ═══════════════════════════════════════════════════════════════════════════
// clasificarTraficoConversion — clasificador puro del dashboard Tráfico y
// Conversión (ADR 049). ESPEJO de la lógica de la vista: mismas medianas y los
// mismos 3 grupos. 3 secciones del catálogo:
//   potenciar    → crear_ad              (CVR orgánico alto, SIN ads → sumar pauta)
//   sin_trafico  → crear_ad              (activas sin ads y sin visitas → visibilizar)
//   revisar_cvr  → revisar_competitividad (alto tráfico, CVR bajo → competitividad)
//
// PARIDAD CON EL AGENTE: función pura, mismo input que arma el loader. La vista
// y el agente construyen `rows` con los mismos campos (visitas, ventas, conAd).
// ═══════════════════════════════════════════════════════════════════════════

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)

function median(arr) {
  const a = arr.filter((x) => x != null && Number.isFinite(x)).sort((x, y) => x - y)
  if (a.length === 0) return null
  const mid = Math.floor(a.length / 2)
  return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2
}

const externalIdDe = (p) => String(p?.externalId || p?.itemId || p?.id || '')
const esActiva = (p) => (p?.estado || p?.status) === 'activa' || p?.status === 'active'

function buildRow(p, ventasPorItem, adsPorItem) {
  const externalId = externalIdDe(p)
  const visitas = num(p?.datosCanal?.visitasRecientes?.last30 ?? p?._visitas30d)
  const ventas = num(ventasPorItem[externalId])
  const ads = adsPorItem[externalId] || { clicks: 0, units: 0 }
  const pagas = Math.min(ads.clicks, visitas)
  const conAd = p?.facetas?.conAd === true || ads.clicks > 0
  const cvr = visitas > 0 ? ventas / visitas : null
  return {
    externalId,
    titulo: p.titulo || p.title || externalId,
    marca: p.marca || '(sin marca)',
    precio: num(p.precio ?? p.price),
    estado: p.estado || (p.status === 'active' ? 'activa' : p.status) || null,
    permalink: p.urlPublica || p?.datosCanal?.permalink || p.permalink || null,
    productoId: p._productoId || p.productoId || null,
    visitas, ventas, cvr, conAd,
    // Flywheel (14-jul-2026): margen y stock para no prender sobre pérdida /
    // sin stock. Acepta shape legacy (_rentabilidadInferida) y canónico.
    margenPct: p?._rentabilidadInferida?.margenPctProm ?? p?.rentabilidadInferida?.margenPctProm ?? null,
    stock: (() => { const s = p?.availableQuantity ?? p?.available_quantity ?? p?.stockDisponible; return s == null ? null : num(s) })(),
  }
}

function itemDe(r, motivo) {
  return {
    id: r.externalId,
    itemId: r.externalId,
    _itemId: r.externalId,
    _titulo: r.titulo,
    title: r.titulo,
    _permalink: r.permalink,
    permalink: r.permalink,
    _productoId: r.productoId,
    _precio: r.precio,
    _motivo: motivo,
    metricas: {
      visitas: r.visitas,
      ventas: r.ventas,
      cvr: r.cvr,
      cvrPct: r.cvr != null ? Math.round(r.cvr * 1000) / 10 : null,
    },
  }
}

/**
 * @param {object}   input
 * @param {Array}    input.publicaciones   — pubs (canónicas o legacy con _visitas30d)
 * @param {Array}    input.ads             — [{ refExternalId, metricas:{clicks, units_quantity} }]
 * @param {object}   input.ventasPorItem   — { [externalId]: unidades facturables }
 * @param {object}   input.config          — umbrales por empresa (futuro)
 */

import { gateAdsPrender, construirInfoRentabilidadPorItem } from './gatePromoRentabilidad'
function normalizeRow(r) {
  const visitas = num(r.visitas)
  const ventas = num(r.ventas)
  return {
    externalId: String(r.externalId || r.itemId || r.id || ''),
    titulo: r.titulo || r.title || String(r.externalId || ''),
    marca: r.marca || '(sin marca)',
    precio: num(r.precio ?? r.price),
    estado: r.estado || (r.status === 'active' ? 'activa' : r.status) || null,
    permalink: r.permalink || r.urlPublica || null,
    productoId: r.productoId || r._productoId || null,
    visitas, ventas,
    cvr: r.cvr != null ? r.cvr : (visitas > 0 ? ventas / visitas : null),
    conAd: r.conAd === true,
    margenPct: r.margenPct ?? null,
    stock: r.stock != null ? num(r.stock) : null,
  }
}

export function clasificarTraficoConversion({ publicaciones = [], ads = [], ventasPorItem = {}, rows: rowsPre = null, config = {}, ecosistema = null, impuestosEmpresa = null, productos = [], catalogoLinks = [], categorias = [], marcas = [], proveedores = [], productoSourcing = [], rentabilidadEmpresa = null } = {}) {
  // 15-jul: corte por cascada ADR 032 para el gate de ads (potenciar/sin_trafico).
  const infoRentPorItemAds = construirInfoRentabilidadPorItem({
    productos, catalogoLinks, categorias, marcas, proveedores, productoSourcing,
    empresaMin: rentabilidadEmpresa?.rentabilidadMinima ?? null,
  })
  // Índice de ads por publicación (clicks para inferir pagas/conAd).
  const adsPorItem = {}
  for (const a of ads) {
    const key = a?.refExternalId || a?.externalId || a?.itemId
    if (!key) continue
    const m = a.metricas || a
    const p = adsPorItem[key] || { clicks: 0, units: 0 }
    p.clicks += num(m.clicks)
    p.units += num(m.units_quantity ?? m.units)
    adsPorItem[key] = p
  }

  const rows = (Array.isArray(rowsPre) && rowsPre.length)
    ? rowsPre.map(normalizeRow)
    : publicaciones.map((p) => buildRow(p, ventasPorItem, adsPorItem))

  // Referencias (medianas) sobre el universo. La mediana de CVR se toma sobre
  // las pubs que CONVIERTEN (venta > 0): es la línea divisoria potenciar/revisar.
  const conVenta = rows.filter((r) => r.ventas > 0 && r.cvr != null)
  const conVis = rows.filter((r) => r.visitas > 0)
  const cvrRef = median(conVenta.map((r) => r.cvr))
  const visRef = median(conVis.map((r) => r.visitas))

  const potenciar = []
  const revisar_cvr = []
  const sin_trafico = []
  // Flywheel (14-jul-2026, docs/flywheel-mercadolibre.md): las secciones que
  // PRENDEN (crear_ad) excluyen pérdida conocida y sin stock — declarado.
  let aPerdida = 0
  let adSinEspacio = 0
  let sinStockAd = 0

  for (const r of rows) {
    if (r.estado !== 'activa') continue
    const prenderVetado = () => {
      // GATE DE ADS (15-jul): margen real − ACOS esperado (cuenta → eco).
      // Las rows normalizadas llevan la clave en externalId (buildRow/normalizeRow).
      const rentAd = infoRentPorItemAds.get(String(r.externalId || r.itemId || r.id || '')) || { costoUnit: null, corte: null }
      const gateAd = gateAdsPrender({
        precio: r.precio,
        margenReal: (r.margenPct != null && Number.isFinite(r.margenPct)) ? r.margenPct : null,
        costoUnit: rentAd.costoUnit, corte: rentAd.corte,
        ecosistema, impuestosEmpresa,
        benchmarksEmpresa: config?._benchmarksML || null,
      })
      if (gateAd.estado === 'veto_perdida') { aPerdida++; return true }
      if (gateAd.estado === 'sin_espacio') { adSinEspacio++; return true }
      if (r.stock != null && r.stock <= 0) { sinStockAd++; return true }
      return false
    }
    // Potenciar: sin ads y CVR por encima de la mediana → traerles tráfico (crear ad).
    if (cvrRef != null && !r.conAd && r.cvr != null && r.cvr > cvrRef) {
      if (prenderVetado()) continue
      potenciar.push(itemDe(r, `CVR ${(r.cvr * 100).toFixed(1)}% (sobre la mediana ${(cvrRef * 100).toFixed(1)}%) y sin publicidad — sumar pauta para traer tráfico`))
      continue
    }
    // Sin tráfico: sin ads y sin visitas → invisible (crear ad para visibilizar).
    if (!r.conAd && (r.visitas || 0) <= 0) {
      if (prenderVetado()) continue
      sin_trafico.push(itemDe(r, 'Activa, sin publicidad y sin visitas en el período — invisible; sumar pauta para visibilizarla'))
      continue
    }
    // Revisar: alto tráfico pero CVR bajo → problema de competitividad.
    if (cvrRef != null && visRef != null && r.visitas > 0 && r.cvr != null && r.visitas > visRef && r.cvr < cvrRef) {
      revisar_cvr.push(itemDe(r, `Mucho tráfico (${r.visitas} visitas, sobre la mediana ${Math.round(visRef)}) pero CVR ${(r.cvr * 100).toFixed(1)}% bajo la mediana — revisar competitividad`))
    }
  }

  potenciar.sort((a, b) => (b.metricas.cvr || 0) - (a.metricas.cvr || 0))
  revisar_cvr.sort((a, b) => (b.metricas.visitas || 0) - (a.metricas.visitas || 0))
  sin_trafico.sort((a, b) => (b._precio || 0) - (a._precio || 0))

  return {
    secciones: { potenciar, sin_trafico, revisar_cvr },
    analisis: {
      kpis: {
        publicaciones: publicaciones.length,
        activas: rows.filter((r) => r.estado === 'activa').length,
        potenciar: potenciar.length,
        sinTrafico: sin_trafico.length,
        revisar: revisar_cvr.length,
      },
      refs: { cvrRef, visRef },
    },
    excluidos: {
      aPerdida,
      adSinEspacio,  // gate ads 15-jul: proyectado abajo de la mínima     // flywheel: a pérdida no se publicita (CN la trabaja)
      sinStockAd,   // flywheel: sin stock no se publicita (Velocidad repone)
    },
  }
}
